import { FC, useState, useRef, useEffect } from 'react';
import { CustomInputProps } from './types';
import classNames from 'classnames';
import { ValidationError } from '../ValidationError';
import { useRenderIcons } from 'hooks/useRenderIcons';
import { CloseIcon } from 'assets/icons';

export const CustomInput: FC<CustomInputProps> = ({
  value = '',
  onChange,
  label,
  maxValue = 99999999.99,
  decimal = 2, // Значение по умолчанию 2 знака после запятой
  errorMessage,
  textFormatError = 'Неверный формат ввода',
  iconWrapperClassName,
  isClearable = false,
  disabled = false,
  icons,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [formatError, setFormatError] = useState<string | null>(null);

  // Форматируем с пробелами только при отображении (не в фокусе)
  const formatDisplayValue = (val: string) => {
    if (!val || isFocused) return val;

    const [integer, decimalPart = ''] = String(val).split('.');
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return decimalPart ? `${formattedInteger}.${decimalPart}` : `${formattedInteger}.00`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value.replace(/[^\d.]/g, '');
    const selectionStart = e.target.selectionStart;

    // Проверка на максимальное значение
    if (!isNaN(Number(newValue)) && Number(newValue) > maxValue) {
      setFormatError(textFormatError);
      return;
    } else {
      setFormatError(null);
    }

    // Проверяем количество точек
    const dotCount = (newValue.match(/\./g) || []).length;
    if (dotCount > 1) return;

    // Если есть точка, ограничиваем дробную часть
    if (dotCount === 1) {
      const parts = newValue.split('.');
      if (parts[1].length > decimal) {
        setFormatError(textFormatError);

        newValue = `${parts[0]}.${parts[1].slice(0, decimal)}`;
      }
    }

    // Проверяем максимальное значение
    const numericValue = parseFloat(newValue);
    if (!isNaN(numericValue) && numericValue > maxValue) return;

    // Сохраняем позицию курсора с учетом удаленных символов
    const diff = e.target.value.length - newValue.length;
    const newCursorPos = Math.max(0, (selectionStart || 0) - diff);
    setCursorPos(newCursorPos);
    onChange?.(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // При фокусе показываем значение без форматирования
    onChange?.(value.replace(/\s/g, '').replace(/\.00$/, ''));
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!value) return;

    // Форматируем при потере фокуса
    const [integer, decimalPart = ''] = value.split('.');
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const formattedDecimal = decimalPart ? decimalPart.slice(0, decimal) : '00';

    onChange?.(`${formattedInteger}.${formattedDecimal}`);
  };

  // Восстанавливаем позицию курсора
  useEffect(() => {
    if (cursorPos !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPos, cursorPos);
    }
  }, [value, cursorPos]);

  const renderIcons = useRenderIcons(
    disabled,
    isClearable
      ? [
          {
            icon: (
              <CloseIcon
                className={classNames('-ml-4 text-text-50 hover:text-text-100', {
                  'cursor-not-allowed': disabled,
                })}
              />
            ),
            onClick: () =>
              onChange &&
              onChange({
                target: {
                  value: '',
                },
              } as any),
          },
          ...(icons ?? []),
        ]
      : [...(icons ?? [])],
    iconWrapperClassName
  );

  return (
    <div>
      <div className="flex">
        <div className="flex grow flex-col justify-end pb-1">
          {label && <div className="body-normal mb-1 !text-text-50">{label}</div>}
          <input
            ref={inputRef}
            type="text"
            value={formatDisplayValue(value)}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={classNames(
              'headline-normal border-0 border-b-2 border-solid border-light-50 !text-text-100 focus:border-primary-60',
              { '!border-danger': errorMessage }
            )}
          />
        </div>
        {renderIcons}
      </div>
      {errorMessage && <ValidationError errorMessage={errorMessage} />}
      {formatError && <ValidationError errorMessage={formatError} />}
    </div>
  );
};
